import React, { useState } from "react";
import { UseFormReturn, useWatch } from "react-hook-form";
import ProcessWheelIcon from "@/modules/hub/features/ResourceLibraryV2/components/FilterSidebar/ProcessWheelCard/ProcessWheelIcon";
import ProcessWheelMiniWheel from "@/modules/hub/features/ResourceLibraryV2/components/FilterSidebar/ProcessWheelCard/ProcessWheelMiniWheel";
import ProcessWheelModal from "@/modules/hub/features/ResourceLibraryV2/components/FilterSidebar/ProcessWheelModal/ProcessWheelModal";
import { FilterSidebarFormData } from "@/modules/hub/features/ResourceLibraryV2/hooks/FilterSidebarFormSchema";
import { ProcessWheelFacet } from "@/modules/hub/features/ResourceLibraryV2/api/getResourceLibratyMetadata/types";
import {
  WHEEL_STAGES,
  WHEEL_WEDGES,
} from "@/modules/hub/features/ResourceLibraryV2/components/FilterSidebar/processWheelSegments";

interface ProcessWheelCardProps {
  form: UseFormReturn<FilterSidebarFormData>;
  processWheels: ProcessWheelFacet[];
}

// Names that actually correspond to a segment on the wheel.
const WHEEL_SEGMENT_NAMES = new Set(
  [...WHEEL_STAGES, ...WHEEL_WEDGES].map((segment) => segment.wheelName),
);

const ProcessWheelCard: React.FC<ProcessWheelCardProps> = ({
  form,
  processWheels,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Re-render whenever the process-wheel selection changes.
  const selectedProcessWheels =
    useWatch({ control: form.control, name: "processWheels" }) ?? [];

  const activeWheelNames = selectedProcessWheels
    .map((wheel) => wheel.name)
    .filter((name) => WHEEL_SEGMENT_NAMES.has(name));

  const hasActive = activeWheelNames.length > 0;
  const activeLabel =
    activeWheelNames.length === 1
      ? activeWheelNames[0]
      : `${activeWheelNames.length} areas selected`;

  return (
    <div className="mb-6 pl-2 pr-4">
      <h2 className="mb-2 text-base font-medium text-gray-900 dark:text-slate-100">
        Process Wheel
      </h2>
      <button
        aria-label={
          hasActive
            ? `Change area — currently ${activeLabel}`
            : "Open the process wheel"
        }
        className="relative w-full overflow-hidden rounded-xl border border-amber-400/70 bg-gradient-to-br from-amber-50 to-white p-4 text-left transition-colors hover:border-amber-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 dark:border-amber-500/50 dark:from-slate-800 dark:to-slate-900"
        onClick={() => setIsModalOpen(true)}
        type="button"
      >
        <div className="flex items-center gap-3">
          {hasActive ? (
            <ProcessWheelMiniWheel activeWheelNames={activeWheelNames} />
          ) : (
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-amber-400 dark:bg-amber-500">
              <ProcessWheelIcon className="h-6 w-6 text-white" />
            </div>
          )}
          <div className="min-w-0">
            {hasActive ? (
              <>
                <h3 className="truncate text-sm font-semibold text-gray-900 dark:text-white">
                  {activeLabel}
                </h3>
                <p className="text-xs font-semibold uppercase tracking-wider text-amber-500 dark:text-amber-400">
                  Tap to change area
                </p>
              </>
            ) : (
              <>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                  Process Wheel
                </h3>
                <p className="text-xs text-gray-500 dark:text-slate-400">
                  {"Browse by the step you're working on"}
                </p>
              </>
            )}
          </div>
        </div>
      </button>

      <ProcessWheelModal
        form={form}
        onOpenChange={setIsModalOpen}
        open={isModalOpen}
        processWheels={processWheels}
      />
    </div>
  );
};

export default ProcessWheelCard;
